/**
 * VRG-700 — paying deals move to Won automatically (Spencer, 2026-10-13; the rules are fixed).
 * VRG-727 (Spencer, 2026-10-20) changed both halves; the definitions now live in gtm/paying.ts:
 *   - WON is the first paid invoice, so the automatic Won moves a deal only on one;
 *   - PAYING ("Paying now") needs money to have ARRIVED: the list-price leg is gone.
 *
 * ── "PAYING NOW" (`payingVerdict`) ───────────────────────────────────────────────────────
 * A subscription is paying now when ALL of these hold:
 *   - it is a LIVE-mode Stripe subscription. Test mode never counts;
 *   - its status is `active` — not trialing, past_due, incomplete or canceled;
 *   - it is not in a trial (see the trap below);
 *   - it is a real subscription (not an intro-seeded sample row);
 *   - AND a live-mode PAID INVOICE above $0 for it (or, with no subscription on the invoice, for
 *     its company) landed in the last PAYING_INVOICE_WINDOW_DAYS. It LAPSES when none has: the
 *     company then reads "Not paying". Before VRG-727 a priced, un-comped subscription counted
 *     with no invoice at all (the "list price" leg); now the price only decides what it is WORTH
 *     (`basis`), never whether it is paying. A $0 plan with no paid invoice is still a PILOT.
 *
 * ⚠️ THE MIRROR CAN SAY `active` FOR A TRIALING SUBSCRIPTION. `company_subscriptions` mirrors
 * Stripe through the `customer.subscription.*` webhooks, and the `created` handler
 * (`StripeSubscriptionProvider.handleSubscriptionCreated`) writes ACTIVE whatever Stripe's own
 * status was — while it does record `trial_end`. So "active" alone is not "not trialing": a
 * `trial_end` in the future means trialing, whatever the status column says.
 *
 * ⚠️ LIVEMODE IS NOT IN THE MIRROR BEFORE VRG-700. Migration 0153 adds
 * `company_subscriptions.stripe_livemode`, written from each subscription event's own
 * `livemode`. A row no event has touched since carries NULL, and is read as the mode of the
 * API's own Stripe key: a subscription can only exist in the account (and mode) of the key that
 * created it, so on dev (test key) every NULL row is test mode and on prod (live key) live.
 *
 * ── THE MOVE ─────────────────────────────────────────────────────────────────────────────
 * A deal whose company has a subscription with a FIRST PAID INVOICE (live, above $0) and is not
 * Won moves to Won (`autoWonDecision`), dated at that invoice. Lost and archived deals move too
 * (they are reopened first). The move is recorded against the subscription id that caused it. A
 * person may move it back where the evidence allows; that records an OVERRIDE for that
 * subscription, and Vera never moves the deal again for the SAME subscription. Lapsing never
 * moves a deal out of Won: nothing here ever moves a deal backwards.
 */
import {isWinningPaidInvoice, PAYING_INVOICE_WINDOW_DAYS} from './paying.js';
import type {DealClosedReason, DealStatus, SalesStage} from './sales-pipeline.js';

/** One subscription as the mirror holds it, with what "paying" needs. */
export interface PayingSubscriptionInput {
  stripe_subscription_id: string | null;
  /** `company_subscriptions.status` (the Stripe mirror). */
  status: string;
  /** From the subscription's own events (0153). Null = never seen on one: the key's mode. */
  livemode: boolean | null;
  /** ISO, or null. In the future = trialing, whatever `status` says. */
  trial_end: string | null;
  /** The plan's list price, cents, per its interval. */
  amount_cents: number | null;
  /** An ACTIVE base-fee comp covers it now (VRG-247: a 100% coupon). */
  comped: boolean;
  /** An intro-seeded sample row (`example_kind`). Never a contract. */
  example: boolean;
  /**
   * The plan bills usage (a metered price) — so a $0 fixed price is NOT a pilot by itself; it
   * pays through its invoices. Optional: absent reads as not metered.
   */
  metered?: boolean;
  /**
   * The latest live-or-unknown-mode PAID invoice above $0 for this subscription (or its company,
   * when the invoice names no subscription), if any. Null = none recorded.
   */
  recent_invoice?: {amount_cents: number; paid_at: string; livemode: boolean | null} | null;
}

/** `no_paid_invoice` (VRG-727): the automatic Won's own reason — the subscription has never been paid. */
export const NOT_PAYING_REASONS = ['test_mode', 'not_active', 'trialing', 'zero_amount', 'comped', 'no_recent_invoice', 'no_paid_invoice', 'example', 'no_stripe_id'] as const;
export type NotPayingReason = (typeof NOT_PAYING_REASONS)[number];

export const NOT_PAYING_REASON_LABELS: Readonly<Record<NotPayingReason, string>> = {
  test_mode: 'Stripe test mode',
  not_active: 'Not active',
  trialing: 'In a trial',
  zero_amount: '$0 plan, no paid invoice',
  comped: 'Comped (base fee waived), no paid invoice',
  no_recent_invoice: 'No paid invoice in the last 60 days',
  no_paid_invoice: 'No paid invoice yet',
  example: 'Sample subscription',
  no_stripe_id: 'No Stripe subscription yet',
};

/**
 * What a paying-now subscription is WORTH: its plan's fixed price (priced, not comped), or its
 * latest paid invoice annualised (a usage-billed or comped-base plan). VRG-727: both need a
 * recent paid invoice to be paying at all; the basis only prices it.
 */
export type PayingBasis = 'list_price' | 'paid_invoice';

export type PayingVerdict = {paying: true; basis: PayingBasis} | {paying: false; reason: NotPayingReason};

/** A $0 PILOT: live, active, not trialing, but nothing is paid (no price, no recent invoice). */
export const isPilotReason = (r: NotPayingReason | null | undefined): boolean => r === 'zero_amount' || r === 'comped';

/**
 * Is this subscription PAYING NOW? The ONE definition (VRG-727): the Paying now chip and filter,
 * the board's warning icon, Committed, and the master pipeline's value rule all read it. (The
 * automatic Won no longer does: it waits for a first paid invoice, `firstPaidSubscriptions`.)
 */
export const payingVerdict = (
  s: PayingSubscriptionInput,
  ctx: {now: Date; envLivemode: boolean},
): PayingVerdict => {
  if (s.example) return {paying: false, reason: 'example'};
  if (!s.stripe_subscription_id) return {paying: false, reason: 'no_stripe_id'};
  const live = s.livemode ?? ctx.envLivemode;
  if (!live) return {paying: false, reason: 'test_mode'};
  if (s.status === 'trialing') return {paying: false, reason: 'trialing'};
  if (s.status !== 'active') return {paying: false, reason: 'not_active'};
  if (s.trial_end && Date.parse(s.trial_end) > ctx.now.getTime()) return {paying: false, reason: 'trialing'};
  const priced = typeof s.amount_cents === 'number' && s.amount_cents > 0;
  // Money has to have arrived: a live paid invoice above $0 inside the window.
  if (hasRecentPaidInvoice(s, ctx)) return {paying: true, basis: priced && !s.comped ? 'list_price' : 'paid_invoice'};
  if (s.comped) return {paying: false, reason: 'comped'};
  // Priced, or usage-billed, but nothing paid in the window: lapsed (or not invoiced yet).
  if (priced || s.metered) return {paying: false, reason: 'no_recent_invoice'};
  return {paying: false, reason: 'zero_amount'};
};

/** A live (or, unrecorded, the key's mode) paid invoice above $0 inside the window. */
export const hasRecentPaidInvoice = (s: Pick<PayingSubscriptionInput, 'recent_invoice'>, ctx: {now: Date; envLivemode: boolean}): boolean => {
  const inv = s.recent_invoice;
  if (!inv || !isWinningPaidInvoice(inv, ctx)) return false;
  const age = ctx.now.getTime() - Date.parse(inv.paid_at);
  return Number.isFinite(age) && age >= -86_400_000 && age <= PAYING_INVOICE_WINDOW_DAYS * 86_400_000;
};

/** A subscription as the automatic Won reads it: its FIRST live paid invoice above $0, if any. */
export interface FirstPaidSubscriptionInput {
  stripe_subscription_id: string | null;
  /** The subscription's own mode (0153), null = the key's mode. */
  livemode: boolean | null;
  example: boolean;
  /** List price, cents, and interval: what the move's preview prints as ARR. */
  amount_cents: number | null;
  interval: string | null;
  first_invoice: {amount_cents: number; paid_at: string; livemode: boolean | null} | null;
}

/**
 * VRG-727 — the subscriptions that can move a deal to Won: each with a first paid invoice that
 * counts (`isWinningPaidInvoice`: live, above $0) on a live, real subscription. `since` is that
 * invoice's date, so the move is dated at it and a LATER deal moves only for a subscription
 * first paid after it opened (`eligibleSubscriptionsForDeal`).
 */
export const firstPaidSubscriptions = (rows: readonly FirstPaidSubscriptionInput[], ctx: {envLivemode: boolean}): PayingSubscription[] => {
  const out: PayingSubscription[] = [];
  for (const r of rows) {
    if (r.example || !r.stripe_subscription_id) continue;
    if ((r.livemode ?? ctx.envLivemode) !== true) continue;
    if (!r.first_invoice || !isWinningPaidInvoice(r.first_invoice, ctx)) continue;
    const priced = typeof r.amount_cents === 'number' && r.amount_cents > 0;
    out.push({
      stripe_subscription_id: r.stripe_subscription_id,
      since: r.first_invoice.paid_at,
      arr: listPriceArr(priced ? r.amount_cents : r.first_invoice.amount_cents, r.interval),
      basis: priced ? 'list_price' : 'paid_invoice',
    });
  }
  return out;
};

/**
 * A paying subscription's ARR, USD: the fixed list price when that is what pays; otherwise the
 * latest paid invoice annualised on the plan's interval (month × 12, year as is) — a usage or
 * comped-base subscription is worth what it actually bills, not $0.
 */
export const payingArr = (s: PayingSubscriptionInput, interval: string | null, basis: PayingBasis): number =>
  basis === 'list_price' ? listPriceArr(s.amount_cents, interval) : listPriceArr(s.recent_invoice?.amount_cents ?? 0, interval);

export const isPayingSubscription = (s: PayingSubscriptionInput, ctx: {now: Date; envLivemode: boolean}): boolean =>
  payingVerdict(s, ctx).paying;

/**
 * The mode of a Stripe secret key, from its MODE TOKEN only (`sk_live_…` / `rk_live_…`), never
 * from key material (working-conventions §70). An unset or test key is test mode.
 */
export const stripeKeyIsLive = (key: string | null | undefined): boolean => /^(sk|rk)_live_/.test((key ?? '').trim());

/** A paying (or, for the move, first-paid) subscription. */
export interface PayingSubscription {
  stripe_subscription_id: string;
  /**
   * ISO, or null. For the board's paying-now rows: when the subscription started (the mirror
   * row's creation). For the automatic Won (VRG-727): its first paid invoice.
   */
  since: string | null;
  /** Its ARR, USD: list price, or the latest paid invoice annualised (`payingArr`). */
  arr: number;
  /** Optional so an older caller reads as list price. */
  basis?: PayingBasis;
}

/** The deal the move would act on. */
export interface AutoWonDealState {
  stage: SalesStage;
  status: DealStatus;
  closed_reason: DealClosedReason | null;
  /** Subscriptions a person has already overridden an automatic Won for, on THIS deal. */
  overridden_subscription_ids: readonly string[];
}

export type AutoWonSkip = 'already_won' | 'not_paying' | 'overridden';

export type AutoWonDecision =
  | {action: 'none'; reason: AutoWonSkip}
  | {action: 'move'; subscription: PayingSubscription; reopen: boolean};

/**
 * Would this deal move to Won? Pure, so the guard pins every case. `paying` is the company's
 * FIRST-PAID subscriptions (`firstPaidSubscriptions`, VRG-727), not its paying-now ones:
 *   - already Won (open, at Won) → nothing (whoever put it there, invoice, person or Vera);
 *   - no first-paid subscription → nothing (`not_paying`: no paid invoice yet; and a lapse never
 *     moves a deal back out);
 *   - every one already overridden on this deal → nothing;
 *   - otherwise → move, for the earliest-paid subscription not overridden, reopening the deal
 *     first when it is closed (Lost or archived).
 */
export const autoWonDecision = (deal: AutoWonDealState, paying: readonly PayingSubscription[]): AutoWonDecision => {
  if (deal.status === 'open' && deal.stage === 'paid_client') return {action: 'none', reason: 'already_won'};
  if (paying.length === 0) return {action: 'none', reason: 'not_paying'};
  const overridden = new Set(deal.overridden_subscription_ids);
  const eligible = paying
    .filter(p => !overridden.has(p.stripe_subscription_id))
    .sort((a, b) => (a.since ?? '9999').localeCompare(b.since ?? '9999') || a.stripe_subscription_id.localeCompare(b.stripe_subscription_id));
  if (eligible.length === 0) return {action: 'none', reason: 'overridden'};
  return {action: 'move', subscription: eligible[0], reopen: deal.status === 'closed'};
};

// ── Several deals per company (VRG-701) ──────────────────────────────────────────────────

/** One of a company's deals, as the company-level rule needs it. */
export interface AutoWonCompanyDeal {
  id: string;
  stage: SalesStage;
  status: DealStatus;
  closed_reason: DealClosedReason | null;
  /** ISO. */
  opened_at: string;
  /** ISO, or null while open. */
  closed_at: string | null;
}

/** Won = open at Won (a Won deal later closed is no longer Won). */
const isWonDeal = (d: Pick<AutoWonCompanyDeal, 'stage' | 'status'>): boolean => d.status === 'open' && d.stage === 'paid_client';

/**
 * VRG-701 — the deal a paying subscription moves to Won, when a company has several (Spencer,
 * 2026-10-14, refined the same day):
 *   1. the most recently opened open, non-Won deal that opened BEFORE the subscription started
 *      (`subscriptionSince`, with the sync's minute of slack) — the sale that subscription closed;
 *   2. else the most recently opened open, non-Won deal;
 *   3. else the most recent non-Won deal of any status (Lost or archived: it is reopened first),
 *      most recently closed first;
 *   4. else the latest deal (it is Won, so `autoWonDecision` says `already_won`).
 * Without a start date (or called without one) step 1 is skipped. So with a pilot opened in March
 * and a rollout opened in September, a subscription that started in June wins the PILOT, and one
 * that started in October wins the rollout. One deal per subscription per decision.
 */
export const autoWonTargetDeal = <D extends AutoWonCompanyDeal>(deals: readonly D[], subscriptionSince?: string | null): D | null => {
  const byOpenedDesc = (a: D, b: D) => b.opened_at.localeCompare(a.opened_at) || b.id.localeCompare(a.id);
  const openNotWon = deals.filter(d => d.status === 'open' && !isWonDeal(d)).sort(byOpenedDesc);
  if (subscriptionSince) {
    const start = Date.parse(subscriptionSince) + LATER_DEAL_SUBSCRIPTION_TOLERANCE_MS;
    const before = openNotWon.find(d => Date.parse(d.opened_at) <= start);
    if (before) return before;
  }
  if (openNotWon[0]) return openNotWon[0];
  const closed = deals
    .filter(d => d.status === 'closed')
    .sort((a, b) => (b.closed_at ?? '').localeCompare(a.closed_at ?? '') || byOpenedDesc(a, b));
  if (closed[0]) return closed[0];
  return [...deals].sort(byOpenedDesc)[0] ?? null;
};

/**
 * VRG-701 — which paying subscriptions may move a given deal:
 *   - none that is SPENT at the company: Vera already moved some deal of this company for it, or
 *     a person overrode such a move (on any of its deals). Before VRG-701 "spent" was per deal,
 *     which with one deal per company is the same thing; per company it means a subscription
 *     that won the pilot never also wins the rollout;
 *   - for the company's FIRST deal, any other (unchanged VRG-700 behaviour, backfill included);
 *   - for a LATER deal, only one that started AFTER the deal opened (less the sync's minute). A
 *     plan upgrade on the SAME subscription therefore never auto-wins an upsell: that is moved by
 *     hand (decision for Spencer, VRG-701).
 */
export const LATER_DEAL_SUBSCRIPTION_TOLERANCE_MS = 60_000;
export const eligibleSubscriptionsForDeal = (
  paying: readonly PayingSubscription[],
  deal: {opened_at: string; is_first_deal: boolean},
  spentAtCompany: readonly string[],
): PayingSubscription[] => {
  const spent = new Set(spentAtCompany);
  const floor = Date.parse(deal.opened_at) - LATER_DEAL_SUBSCRIPTION_TOLERANCE_MS;
  return paying.filter(p => !spent.has(p.stripe_subscription_id) && (deal.is_first_deal || (!!p.since && Date.parse(p.since) >= floor)));
};

/**
 * VRG-701 — which deal each subscription's money belongs to, so a company with two deals does not
 * count one subscription twice (the master pipeline's actual, Committed, "Won, but no active paid
 * subscription"). Pure, so the guard pins it:
 *   1. CLAIMED: a deal Vera moved to Won for this subscription and nobody overrode → that deal;
 *   2. else the company's earliest-opened Won deal;
 *   3. else the deal the automatic Won would move for it (`autoWonTargetDeal`, by its start).
 * With one deal per company every subscription lands on it, which is VRG-700's behaviour.
 */
export const attributeSubscriptions = <D extends AutoWonCompanyDeal>(
  deals: readonly D[],
  subscriptionIds: readonly string[],
  claims: ReadonlyMap<string, string>,
  /** When each subscription started, so step 3 picks the deal that opened before it. */
  sinceBy: ReadonlyMap<string, string | null> = new Map(),
): Map<string, string> => {
  const out = new Map<string, string>();
  if (deals.length === 0) return out;
  const ids = new Set(deals.map(d => d.id));
  const firstWon = deals.filter(isWonDeal).sort((a, b) => a.opened_at.localeCompare(b.opened_at) || a.id.localeCompare(b.id))[0] ?? null;
  for (const sub of subscriptionIds) {
    const claimed = claims.get(sub);
    const fallback = firstWon ?? autoWonTargetDeal(deals, sinceBy.get(sub) ?? null);
    if (claimed && ids.has(claimed)) out.set(sub, claimed);
    else if (fallback) out.set(sub, fallback.id);
  }
  return out;
};

/** The history line, with the date of the first paid invoice (VRG-727; VRG-700 named the start). */
export const autoWonEvidence = (firstPaidAt: string | null): string =>
  `Moved to Won automatically: first paid invoice on ${firstPaidAt ? firstPaidAt.slice(0, 10) : 'an unknown date'}`;

/** The warning on a Won deal with nothing paying behind it (Spencer's wording). */
export const WON_WITHOUT_PAYING_TEXT = 'Won, but no active paid subscription';

/**
 * A Won deal (open, at Won) whose company has no paying-now subscription (VRG-727: none with a
 * paid invoice in 60 days). It still counts as PREDICTED on the master pipeline (VRG-696
 * decision #1): the value rule reads `paying_arr`.
 */
export const isWonWithoutPaying = (d: {stage: SalesStage; status: DealStatus; paying_arr?: number | null}): boolean =>
  d.status === 'open' && d.stage === 'paid_client' && !(typeof d.paying_arr === 'number' && d.paying_arr > 0);

/** A plan's list price → ARR, USD (month × 12, year as is), from cents. */
export const listPriceArr = (amountCents: number | null, interval: string | null): number => {
  const a = (amountCents ?? 0) / 100;
  return Math.round(((interval ?? 'month').toLowerCase().startsWith('year') ? a : a * 12) * 100) / 100;
};

// ── The dry run (`GET /internal/gtm/sales/auto-won/preview`) ─────────────────────────────

/** A deal the next run WOULD move to Won, and why. */
export interface AutoWonCandidate {
  deal_id: string;
  account_id: string;
  account_name: string;
  company_id: string;
  company_name: string;
  from_stage: SalesStage;
  from_status: DealStatus;
  closed_reason: DealClosedReason | null;
  /** It is closed (Lost or archived) and would be reopened first. */
  reopen: boolean;
  subscription_id: string;
  since: string | null;
  arr: number;
  /** The history line it would write. */
  evidence: string;
}

/** A deal with a subscription that would NOT move, and why (so "nothing to do" is checkable). */
export interface AutoWonSkipped {
  deal_id: string;
  account_id: string;
  account_name: string;
  stage: SalesStage;
  status: DealStatus;
  /**
   * `predates_deal` (VRG-701): the company is paying, but every paying subscription started before
   * this deal (a later deal at the company) opened, so it belongs to an earlier deal.
   */
  reason: 'overridden' | 'not_paying' | 'predates_deal';
  /** Each subscription on the company and why it is not paying (for `not_paying`). */
  subscriptions: Array<{subscription_id: string | null; status: string; reason: NotPayingReason | null}>;
}

export interface AutoWonPlan {
  /** `SALES_AUTO_WON_ENABLED`: off = the dry run is all that happens. */
  enabled: boolean;
  /** The API's Stripe key mode: what a subscription with no recorded mode is read as. */
  env_livemode: boolean;
  candidates: AutoWonCandidate[];
  skipped: AutoWonSkipped[];
  computed_at: string;
}

/** What one run did (or, dry, would do). */
export interface AutoWonRunResult extends AutoWonPlan {
  dry_run: boolean;
  trigger: 'webhook' | 'reconcile' | 'manual';
  /** Deals actually moved (empty on a dry run). */
  moved: string[];
}
